import { useEffect, useMemo, useState } from 'react';
import { api, type DistributorWithRoutes, type Stop } from './api';
import { ListActions, PrintHeading } from './CustomerTools';
import {
  cityStateZip,
  describeSort,
  mapLink,
  matchesCustomer,
  matchesRoute,
  nextSort,
  searchTerms,
  sortStops,
  type CustomerRow,
  type SortKey,
  type SortState,
} from './customers';
import { buildCustomerWorkbook, downloadWorkbook, exportFileName } from './exportCustomers';
import { SortableHeader } from './SortableHeader';
import { FindRoute } from './FindRoute';

interface CustomerRoute {
  /** Distributor ID plus route ID: the same route number at two distributors stays apart. */
  key: string;
  distributorId: number;
  distributorName: string;
  routeNumber: string;
  stops: Stop[];
}

interface ShownRoute extends CustomerRoute {
  /** The customers listed for this route after the search and sort. */
  visibleStops: Stop[];
  /** Customers matched by the search, or null when the whole route is listed. */
  matchCount: number | null;
  /** Opened automatically when only some of its customers matched. */
  autoExpand: boolean;
}

type CustomerRoutesState =
  | { status: 'loading' }
  | { status: 'ready'; routes: CustomerRoute[] }
  | { status: 'error'; message: string };

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Every permitted customer, grouped under collapsed route rows. Search,
 * sort, export and print all work on the same list: the rows shown (or
 * hidden only by a collapsed group) are exactly the rows exported and
 * printed, and the count beside the buttons says how many that is.
 */
export function CustomersPage({
  distributors,
  userId,
  accountName,
}: {
  distributors: DistributorWithRoutes[];
  userId: number | null;
  accountName: string;
}) {
  const [search, setSearch] = useState('');
  const [findingRoute, setFindingRoute] = useState(false);
  const [sort, setSort] = useState<SortState | null>(null);
  const [expansion, setExpansion] = useState<Record<string, boolean>>({});
  const [retry, setRetry] = useState(0);
  const [data, setData] = useState<CustomerRoutesState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setData({ status: 'loading' });
    const requested = distributors.flatMap((distributor) =>
      distributor.routes.map((route) => ({
        key: `${distributor.id}:${route.id}`,
        distributorId: distributor.id,
        distributorName: distributor.name,
        routeNumber: route.routeNumber,
      })),
    );
    Promise.all(
      requested.map(async (item) => {
        const { route } = await api.routeDetails(userId, item.distributorId, item.routeNumber);
        return { ...item, stops: route.stops };
      }),
    )
      .then((routes) => !cancelled && setData({ status: 'ready', routes }))
      .catch((error: unknown) => {
        if (!cancelled) {
          setData({
            status: 'error',
            message: error instanceof Error ? error.message : 'Customers could not load.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [distributors, userId, retry]);

  const query = search.trim();
  const searching = query !== '';

  const shown = useMemo<ShownRoute[]>(() => {
    if (data.status !== 'ready') return [];
    const terms = searchTerms(query);
    return data.routes.flatMap((route) => {
      // A route found by its number or distributor lists all of its
      // customers; otherwise only the customers that matched are listed.
      const wholeRoute =
        terms.length === 0 || matchesRoute(route.routeNumber, route.distributorName, terms);
      const matching = wholeRoute
        ? route.stops
        : route.stops.filter((stop) => matchesCustomer(stop, terms));
      if (!wholeRoute && matching.length === 0) return [];
      return [
        {
          ...route,
          visibleStops: sortStops(matching, sort),
          matchCount: wholeRoute ? null : matching.length,
          autoExpand: !wholeRoute,
        },
      ];
    });
  }, [data, query, sort]);

  const shownCustomers = shown.reduce((count, route) => count + route.visibleStops.length, 0);

  const toggleSort = (key: SortKey) => setSort((current) => nextSort(current, key));

  function exportExcel() {
    const rows: CustomerRow[] = shown.flatMap((route) =>
      route.visibleStops.map((stop) => ({
        distributorId: route.distributorId,
        distributorName: route.distributorName,
        routeNumber: route.routeNumber,
        stop,
      })),
    );
    const workbook = buildCustomerWorkbook({
      rows,
      scope: searching
        ? `Customers matching “${query}” on ${plural(shown.length, 'permitted route')}`
        : `All customers on ${plural(shown.length, 'permitted route')}`,
      account: accountName,
      search: query,
      sort: describeSort(sort),
    });
    downloadWorkbook(workbook, exportFileName(searching ? `customers ${query}` : 'customers'));
  }

  return (
    <section className="customers-page" aria-label="Customers">
      <div className="customers-toolbar">
        <div className="customers-page-search">
          <label htmlFor="all-customers-search">Search customers</label>
          <input
            id="all-customers-search"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setExpansion({});
            }}
            placeholder="Customer, address, ZIP, phone, route, or distributor"
          />
        </div>
        <div className="customers-actions" aria-label="Route actions">
          <button
            type="button"
            aria-expanded={findingRoute}
            aria-controls="find-route-panel"
            onClick={() => setFindingRoute((open) => !open)}
          >
            {findingRoute ? 'Close customer check' : 'Find a route'}
          </button>
          <button type="button" disabled title="Coming soon">
            Add a route
          </button>
        </div>
      </div>

      {findingRoute && (
        <div id="find-route-panel">
          <FindRoute key={userId ?? 'signed-in'} userId={userId} />
        </div>
      )}

      {data.status === 'loading' && (
        <div className="panel muted" role="status">
          Loading customers…
        </div>
      )}
      {data.status === 'error' && (
        <div className="panel error" role="alert">
          Customers could not load: {data.message}{' '}
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Try again
          </button>
        </div>
      )}
      {data.status === 'ready' && (
        <>
          <PrintHeading
            title="Customers"
            subtitle={
              searching
                ? `Customers matching “${query}” on ${plural(shown.length, 'route')}`
                : `All customers on ${plural(shown.length, 'route')}`
            }
            account={accountName}
            search={query}
            sort={sort}
            count={shownCustomers}
          />
          <div className="customers-table-summary">
            <span aria-live="polite">
              {plural(shown.length, 'route')} · {plural(shownCustomers, 'customer')}
              {searching ? ' match your search' : ''}
            </span>
            <ListActions
              count={shownCustomers}
              onExport={exportExcel}
              onPrint={() => window.print()}
            />
          </div>
          {shown.length ? (
            <div className="customers-table-scroll">
              <table className="customers-table">
                <caption className="visually-hidden">Customers grouped by route</caption>
                <thead>
                  <tr>
                    <SortableHeader
                      column="name"
                      label="Route / Customer"
                      sortLabel="customer name"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <th scope="col">Distributor</th>
                    <SortableHeader
                      column="street"
                      label="Street address"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortableHeader
                      column="place"
                      label="City, state, ZIP"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortableHeader
                      column="contact"
                      label="Contact"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortableHeader column="phone" label="Phone" sort={sort} onSort={toggleSort} />
                    <th scope="col" className="map-col">
                      Map
                    </th>
                  </tr>
                </thead>
                {shown.map((route) => {
                  const expanded = expansion[route.key] ?? route.autoExpand;
                  const childClass = `customers-child-row${expanded ? '' : ' is-collapsed'}`;
                  return (
                    <tbody key={route.key}>
                      <tr className="customers-route-row">
                        <th scope="row">
                          <button
                            type="button"
                            className="customers-route-toggle"
                            aria-expanded={expanded}
                            onClick={() =>
                              setExpansion((current) => ({
                                ...current,
                                [route.key]: !expanded,
                              }))
                            }
                          >
                            <span className="customers-chevron" aria-hidden="true">
                              {expanded ? '▾' : '▸'}
                            </span>
                            <strong>Route {route.routeNumber}</strong>
                            <span className="customers-route-count">
                              {plural(route.stops.length, 'customer')}
                            </span>
                          </button>
                        </th>
                        <td>{route.distributorName}</td>
                        <td colSpan={5} className="customers-route-hint">
                          {route.matchCount !== null
                            ? plural(route.matchCount, 'matching customer')
                            : ''}
                        </td>
                      </tr>
                      {route.visibleStops.length === 0 && (
                        <tr className={childClass}>
                          <td colSpan={7} className="customers-empty-route">
                            This route has no customers yet.
                          </td>
                        </tr>
                      )}
                      {route.visibleStops.map((stop) => {
                        const link = mapLink(stop);
                        return (
                          <tr className={childClass} key={stop.id}>
                            <th scope="row" className="customers-name-cell">
                              {stop.name}
                            </th>
                            <td className="customers-muted-cell">
                              {route.distributorName}
                              <span className="print-only"> · Route {route.routeNumber}</span>
                            </td>
                            <td>{stop.streetAddress || '—'}</td>
                            <td>{cityStateZip(stop) || '—'}</td>
                            <td>{stop.contactName || '—'}</td>
                            <td>
                              {[stop.primaryPhone, stop.secondaryPhone]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </td>
                            <td className="map-col">
                              {link ? (
                                <a
                                  className="map-link"
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Open a map for ${stop.name}`}
                                >
                                  Map
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  );
                })}
              </table>
            </div>
          ) : (
            <div className="panel empty">
              {searching
                ? `No customers or routes match “${query}”. Try fewer words, part of a name, a street, a ZIP code, or a phone number.`
                : 'No routes are available.'}
            </div>
          )}
        </>
      )}
    </section>
  );
}
